import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { deepClone } from "app/utils/objectHelpers";
import { getDsokSystemSettings, saveDsokSystemSettings } from "app/services/api/dsokSystemSettingsApi";
import TranslationsTopBar from "app/features/dsokCustomerDetail/translations/TranslationsTopBar";
import TranslationsTree from "app/features/dsokCustomerDetail/translations/TranslationsTree";
import TranslationsEditor from "app/features/dsokCustomerDetail/translations/TranslationsEditor";
import {
  useTranslationsState,
  useFilteredTreeAndSelection,
} from "app/features/dsokCustomerDetail/translations/useTranslationsState";
import {
  countMissingForKey,
  validatePlaceholders,
} from "app/features/dsokCustomerDetail/translations/translationsHelpers";

const DSOK_TRANSLATIONS_FILENAME = "dsokTranslations.json";

function getTranslationsFromSettings(settings) {
  const translations = settings?.translations;
  if (translations != null && typeof translations === "object" && !Array.isArray(translations)) {
    return JSON.parse(JSON.stringify(translations));
  }
  return {};
}

export default function NewCustomerTranslationsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDsokSystemSettings()
      .then((data) => {
        if (!cancelled && data != null && typeof data === "object") {
          setOriginalSettings(deepClone(data));
        } else if (!cancelled) {
          setOriginalSettings(deepClone({ translations: {} }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          enqueueSnackbar(t("pages.systemSettings.loadError", { defaultValue: "Nepodarilo sa načítať nastavenia." }), {
            variant: "error",
          });
          setOriginalSettings(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enqueueSnackbar, t]);

  const initialTranslations = useMemo(
    () => getTranslationsFromSettings(originalSettings ?? {}),
    [originalSettings]
  );

  const [search, setSearch] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [modulePrefix, setModulePrefix] = useState("");
  const [showOnlyChanged, setShowOnlyChanged] = useState(false);
  const [selection, setSelection] = useState({
    selectedKeyPath: null,
    selectedGroupKeyPath: null,
  });
  const { selectedKeyPath, selectedGroupKeyPath } = selection;
  const initialSelectionSetRef = useRef(false);
  const userHasSelectedRef = useRef(false);

  const {
    translationsJson,
    setTranslationsJson,
    originalTranslationsJson,
    languages,
    updateValue,
    getMissingCount,
  } = useTranslationsState(initialTranslations, () => { });

  const handleExport = useCallback(() => {
    const json = JSON.stringify(translationsJson, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = DSOK_TRANSLATIONS_FILENAME;
    a.click();
    URL.revokeObjectURL(url);
  }, [translationsJson]);

  const handleImportFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result ?? "{}");
          if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
            enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importError"), {
              variant: "error",
            });
            return;
          }
          setTranslationsJson(JSON.parse(JSON.stringify(parsed)));
          enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importSuccess"), {
            variant: "success",
          });
        } catch {
          enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importError"), {
            variant: "error",
          });
        }
        e.target.value = "";
      };
      reader.readAsText(file, "UTF-8");
    },
    [setTranslationsJson, enqueueSnackbar, t]
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSave = useCallback(async () => {
    if (originalSettings == null) return;
    const { valid, errors: placeholderErrors } = validatePlaceholders(translationsJson);
    if (!valid && placeholderErrors.length > 0) {
      const first = placeholderErrors[0];
      const message = t("pages.systemSettings.translations.placeholderValidationError", {
        defaultValue: "Kľúč \"{{keyPath}}\" ({{lang}}): chýbajú placeholdery {{missing}}",
        keyPath: first.keyPath,
        lang: first.lang,
        missing: first.missing.join(", "),
      });
      enqueueSnackbar(message, { variant: "error" });
      return;
    }
    setSaving(true);
    try {
      const payloadToSave = deepClone(originalSettings);
      payloadToSave.translations = deepClone(translationsJson);
      const response = await saveDsokSystemSettings(payloadToSave);
      const nextOriginal = response != null && typeof response === "object" ? deepClone(response) : deepClone(payloadToSave);
      setOriginalSettings(nextOriginal);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      enqueueSnackbar(t("pages.systemSettings.translationsSaveSuccess", { defaultValue: "Preklady boli uložené" }), {
        variant: "success",
      });
    } catch (err) {
      const message = err?.response?.data?.message ?? err?.message ?? t("pages.systemSettings.saveError", { defaultValue: "Nepodarilo sa uložiť nastavenia." });
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }, [originalSettings, translationsJson, enqueueSnackbar, t]);

  const {
    treeNodes,
    filteredKeyPaths,
    leafKeyPathsUnderGroup,
    hasFilters,
  } = useFilteredTreeAndSelection({
    translationsJson,
    originalTranslationsJson,
    search,
    missingOnly,
    modulePrefix,
    showOnlyChanged,
  });

  const leafKeyPathsInGroup = useMemo(
    () =>
      selectedGroupKeyPath != null
        ? leafKeyPathsUnderGroup(selectedGroupKeyPath)
        : [],
    [selectedGroupKeyPath, leafKeyPathsUnderGroup]
  );

  const getMissingCountForTree = useCallback(
    (keyPath) => {
      const leavesUnder = leafKeyPathsUnderGroup(keyPath);
      if (leavesUnder.length > 0) {
        return leavesUnder.filter(
          (kp) => countMissingForKey(translationsJson, kp, languages) > 0
        ).length;
      }
      return getMissingCount(keyPath);
    },
    [translationsJson, languages, getMissingCount, leafKeyPathsUnderGroup]
  );

  const firstVisibleGroup = useMemo(() => {
    const first = treeNodes?.[0];
    if (first && !first.isLeaf) return first.keyPath;
    const firstLeaf = filteredKeyPaths[0];
    return firstLeaf || null;
  }, [treeNodes, filteredKeyPaths]);

  useEffect(() => {
    if (
      !initialSelectionSetRef.current &&
      firstVisibleGroup != null &&
      treeNodes?.length > 0
    ) {
      initialSelectionSetRef.current = true;
      const node = treeNodes.find(
        (n) => n.keyPath === firstVisibleGroup || n.id === firstVisibleGroup
      );
      if (node?.isLeaf) {
        setSelection({ selectedKeyPath: firstVisibleGroup, selectedGroupKeyPath: null });
      } else {
        setSelection({ selectedKeyPath: null, selectedGroupKeyPath: firstVisibleGroup });
      }
    }
  }, [firstVisibleGroup, treeNodes]);

  useEffect(() => {
    const keyStillVisible =
      selectedKeyPath == null || filteredKeyPaths.includes(selectedKeyPath);
    const groupStillVisible =
      selectedGroupKeyPath == null ||
      filteredKeyPaths.some(
        (kp) => kp === selectedGroupKeyPath || kp.startsWith(`${selectedGroupKeyPath}.`)
      );
    if (!keyStillVisible && selectedKeyPath != null) {
      setSelection((prev) => ({ ...prev, selectedKeyPath: null }));
    }
    if (!groupStillVisible && selectedGroupKeyPath != null) {
      setSelection((prev) => ({ ...prev, selectedGroupKeyPath: null }));
    }
  }, [filteredKeyPaths, selectedKeyPath, selectedGroupKeyPath]);

  const handleSelectLeaf = useCallback((keyPath) => {
    userHasSelectedRef.current = true;
    setSelection({ selectedKeyPath: keyPath, selectedGroupKeyPath: null });
  }, []);

  const handleSelectGroup = useCallback((groupKeyPath) => {
    userHasSelectedRef.current = true;
    setSelection({ selectedKeyPath: null, selectedGroupKeyPath: groupKeyPath });
  }, []);

  const isDirty =
    JSON.stringify(translationsJson) !== JSON.stringify(originalTranslationsJson);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            {t("sidebar.menuItem.home")}
          </Link>
          <Link
            to="/dsok/system-settings"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("pages.systemSettings.breadcrumb", { defaultValue: "Nastavenia" })}
          </Link>
          <Link
            to="/dsok/system-settings/new-customer-settings"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("pages.systemSettings.newCustomerSettingsBreadcrumb", {
              defaultValue: "Nastavenie nového zákazníka",
            })}
          </Link>
          <Typography color="text.primary">
            {t("pages.systemSettings.newCustomerSettingsTabs.translations", {
              defaultValue: "Preklady",
            })}
          </Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
          <Tooltip title={t("common.save", { defaultValue: "Uložiť" })}>
            <span>
              <Button
                color="primary"
                size="small"
                variant="outlined"
                onClick={handleSave}
                disabled={originalSettings == null || !isDirty || saving}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                sx={{
                  ...((originalSettings == null || !isDirty || saving) && {
                    opacity: 0.5,
                    cursor: "not-allowed",
                    "&:hover": { backgroundColor: "transparent" },
                  }),
                }}
              >
                {t("common.save", { defaultValue: "Uložiť" })}
              </Button>
            </span>
          </Tooltip>
          {saveSuccess && (
            <Typography component="span" color="success.main" variant="body2">
              {t("common.saved", { defaultValue: "Uložené." })}
            </Typography>
          )}
        </Stack>
      </Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t("pages.systemSettings.newCustomerSettingsTabs.translations", {
          defaultValue: "Preklady",
        })}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 480,
          height: "calc(100vh - 200px)",
          maxHeight: 900,
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={handleImportFileChange}
        />
        <TranslationsTopBar
          search={search}
          onSearchChange={setSearch}
          missingOnly={missingOnly}
          onMissingOnlyChange={setMissingOnly}
          modulePrefix={modulePrefix}
          onModulePrefixChange={setModulePrefix}
          showOnlyChanged={showOnlyChanged}
          onShowOnlyChangedChange={setShowOnlyChanged}
          onImportClick={handleImportClick}
          onExportClick={handleExport}
        />
        <Alert severity="info" sx={{ mx: 1.5, mt: 1, flexShrink: 0 }}>
          {t("dsokCustomerDetail.translationsUI.exportHelp")}
        </Alert>
        <Alert severity="info" sx={{ mx: 1.5, mt: 0.5, flexShrink: 0 }}>
          {t("dsokCustomerDetail.translationsUI.importHelp")}
        </Alert>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: 280,
              minWidth: 280,
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
            >
              {t("dsokCustomerDetail.translationsUI.treeTitle")}
            </Typography>
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <TranslationsTree
                treeNodes={treeNodes}
                selectedKeyPath={selectedKeyPath}
                selectedGroupKeyPath={selectedGroupKeyPath}
                onSelectLeaf={handleSelectLeaf}
                onSelectGroup={handleSelectGroup}
                getMissingCount={getMissingCountForTree}
                filterApplied={hasFilters}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
            >
              {t("dsokCustomerDetail.translationsUI.editorTitle")}
            </Typography>
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <TranslationsEditor
                selectedKeyPath={selectedKeyPath}
                selectedGroupKeyPath={selectedGroupKeyPath}
                leafKeyPathsInGroup={leafKeyPathsInGroup}
                translationsJson={translationsJson}
                languages={languages}
                onChange={updateValue}
                getMissingCount={getMissingCount}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
